import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import { FiUsers, FiRefreshCw, FiTrendingUp, FiHeart, FiActivity, FiChevronDown, FiChevronUp } from 'react-icons/fi'
import { 
  Container, 
  Card, 
  Grid, 
  Heading1, 
  Heading2, 
  BodyText, 
  SmallText, 
  Button, 
  StatusBadge,
  LoadingSpinner 
} from '../components/ui'
import { BarChart } from '../components/Chart'
import { theme, mediaQuery } from '../theme'
import { analyticsAPI, dataHelpers } from '../services/api'

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: ${theme.spacing.xl};
  
  ${mediaQuery.mobile} {
    flex-direction: column;
    gap: ${theme.spacing.md};
  }
`

const MetricSelector = styled.div`
  display: flex;
  gap: ${theme.spacing.sm};
  margin-bottom: ${theme.spacing.lg};
  overflow-x: auto;
  padding-bottom: ${theme.spacing.sm};
  
  ${mediaQuery.mobile} {
    flex-direction: column;
  }
`

const MetricButton = styled.button`
  background: ${props => props.active ? theme.colors.primary.main : 'white'};
  color: ${props => props.active ? 'white' : theme.colors.primary.main};
  border: 2px solid ${theme.colors.primary.main};
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  border-radius: ${theme.borderRadius.md};
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xs};
  
  &:hover {
    background: ${props => props.active ? theme.colors.primary.dark : theme.colors.primary.light}20;
  }
`

const RankingTable = styled.div`
  background: white;
  border-radius: ${theme.borderRadius.lg};
  overflow: hidden;
  box-shadow: ${theme.shadows.card};
`

const TableHeader = styled.div`
  background: ${theme.colors.primary.main};
  color: white;
  padding: ${theme.spacing.md} ${theme.spacing.lg};
  display: grid;
  grid-template-columns: 60px 1fr 100px 120px;
  gap: ${theme.spacing.md};
  font-weight: 600;
  
  ${mediaQuery.mobile} {
    grid-template-columns: 50px 1fr 80px;
    gap: ${theme.spacing.sm};
    padding: ${theme.spacing.sm} ${theme.spacing.md};
  }
`

const TableRow = styled.div`
  padding: ${theme.spacing.md} ${theme.spacing.lg};
  display: grid;
  grid-template-columns: 60px 1fr 100px 120px;
  gap: ${theme.spacing.md};
  align-items: center;
  border-bottom: 1px solid ${theme.colors.secondary.beige};
  transition: background 0.2s ease;
  
  &:hover {
    background: ${theme.colors.secondary.beige}50;
  }
  
  &:last-child {
    border-bottom: none;
  }
  
  ${mediaQuery.mobile} {
    grid-template-columns: 50px 1fr 80px;
    gap: ${theme.spacing.sm};
    padding: ${theme.spacing.sm} ${theme.spacing.md};
  }
`

const SuperhumanRow = styled(TableRow)`
  background: linear-gradient(135deg, ${theme.colors.status.success}15, ${theme.colors.primary.main}15);
  border: 2px solid ${theme.colors.status.success}30;
  border-radius: ${theme.borderRadius.md};
  margin: 4px 0;
  
  &:hover {
    background: linear-gradient(135deg, ${theme.colors.status.success}25, ${theme.colors.primary.main}25);
  }
`

const RankNumber = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: ${props => {
    if (props.rank === 1) return theme.colors.status.warning; // Gold
    if (props.rank === 2) return theme.colors.primary.light; // Silver
    if (props.rank === 3) return theme.colors.status.error; // Bronze
    return theme.colors.primary.main;
  }};
  text-align: center;
`

const UserInfo = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 220px;
`

const UserIdentity = styled.div`
  margin-bottom: 4px;
`

const UserDisplayName = styled.div`
  font-weight: 600;
  color: ${theme.colors.primary.main};
  font-size: ${theme.typography.body.p2.fontSize};
  margin-bottom: 2px;
`

const UserStats = styled.div`
  font-size: ${theme.typography.body.p3.fontSize};
  color: ${theme.colors.secondary.black};
  display: flex;
  flex-direction: column;
  gap: 2px;
`

const StatLine = styled.div`
  font-size: ${theme.typography.body.p3.fontSize};
  color: ${theme.colors.primary.light};
`

const ScoreDisplay = styled.div`
  font-size: 20px;
  font-weight: 700;
  color: ${theme.colors.primary.main};
  text-align: center;
`

const BadgeColumn = styled.div`
  display: flex;
  justify-content: center;
  
  ${mediaQuery.mobile} {
    display: none;
  }
`

const ConfidenceBadge = styled.div`
  display: inline-flex;
  align-items: center;
  padding: 2px 6px;
  border-radius: ${theme.borderRadius.sm};
  font-size: 10px;
  font-weight: 600;
  margin-left: 8px;
  white-space: nowrap;
  
  ${props => {
    switch (props.level) {
      case 'very-high':
        return `background: ${theme.colors.secondary.green}20; color: ${theme.colors.secondary.green}; border: 1px solid ${theme.colors.secondary.green}40;`;
      case 'high':
        return `background: ${theme.colors.primary.main}20; color: ${theme.colors.primary.main}; border: 1px solid ${theme.colors.primary.main}40;`;
      case 'moderate':
        return `background: ${theme.colors.status.warning}20; color: ${theme.colors.status.warning}; border: 1px solid ${theme.colors.status.warning}40;`;
      case 'low':
        return `background: ${theme.colors.primary.light}20; color: ${theme.colors.primary.light}; border: 1px solid ${theme.colors.primary.light}40;`;
      case 'very-low':
        return `background: ${theme.colors.status.error}20; color: ${theme.colors.status.error}; border: 1px solid ${theme.colors.status.error}40;`;
      default:
        return `background: ${theme.colors.secondary.beige}; color: ${theme.colors.secondary.black}; border: 1px solid ${theme.colors.secondary.black}20;`;
    }
  }}
`

const ConfidenceSection = styled.div`
  margin-bottom: ${theme.spacing.xl};
`

const ConfidenceSectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${theme.spacing.md};
  padding: ${theme.spacing.md} ${theme.spacing.lg};
  background: ${props => {
    switch (props.level) {
      case 'very-high':
        return `linear-gradient(135deg, ${theme.colors.secondary.green}10, ${theme.colors.secondary.green}05)`;
      case 'high':
        return `linear-gradient(135deg, ${theme.colors.primary.main}10, ${theme.colors.primary.main}05)`;
      case 'moderate':
        return `linear-gradient(135deg, ${theme.colors.status.warning}10, ${theme.colors.status.warning}05)`;
      case 'low':
        return `linear-gradient(135deg, ${theme.colors.primary.light}10, ${theme.colors.primary.light}05)`;
      case 'very-low':
        return `linear-gradient(135deg, ${theme.colors.status.error}10, ${theme.colors.status.error}05)`;
      default:
        return `${theme.colors.secondary.beige}40`;
    }
  }};
  border-left: 4px solid ${props => {
    switch (props.level) {
      case 'very-high': return theme.colors.secondary.green;
      case 'high': return theme.colors.primary.main;
      case 'moderate': return theme.colors.status.warning;
      case 'low': return theme.colors.primary.light;
      case 'very-low': return theme.colors.status.error;
      default: return theme.colors.secondary.black;
    }
  }};
  border-radius: ${theme.borderRadius.md};
`

const ConfidenceSectionTitle = styled.div`
  font-weight: 700;
  font-size: ${theme.typography.body.p1.fontSize};
  color: ${theme.colors.primary.main};
`

const ConfidenceSectionDescription = styled.div`
  font-size: ${theme.typography.body.p3.fontSize};
  color: ${theme.colors.primary.light};
  margin-top: 4px;
`

const ConfidenceCount = styled.div`
  font-size: ${theme.typography.body.p2.fontSize};
  font-weight: 600;
  color: ${theme.colors.secondary.black};
`

const RankingsPage = () => {
  const [data, setData] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)
  const [selectedMetric, setSelectedMetric] = useState('resilience')
  const [sortDirection, setSortDirection] = useState('desc')
  
  useEffect(() => {
    loadRankingsData()
  }, [])
  
  const loadRankingsData = async () => {
    try {
      setIsLoading(true)
      setError(null)
      
      const response = await analyticsAPI.getRankings()
      console.log('Rankings API response:', response.data) // Debug logging
      
      // Transform backend rankings structure to frontend expected structure
      const rankingsData = response.data
      const users = transformRankingsToUsers(rankingsData)
      
      console.log('Transformed users for rankings:', users.length) // Debug logging
      console.log('Confidence grouped rankings:', rankingsData.confidenceGroupedRankings) // Debug logging
      setData({ 
        users, 
        totalUsers: rankingsData.totalUsers,
        confidenceGroupedRankings: rankingsData.confidenceGroupedRankings || {}
      })
    } catch (err) {
      setError('Failed to load rankings data')
      console.error('Failed to load rankings data:', err)
    } finally {
      setIsLoading(false)
    }
  }
  
  // Transform backend rankings arrays into unified users array
  const transformRankingsToUsers = (rankingsData) => {
    const userMap = new Map()
    
    // Process mental resilience rankings
    if (rankingsData.mentalResilienceRankings) {
      rankingsData.mentalResilienceRankings.forEach(user => {
        userMap.set(user.userId, {
          userId: user.userId,
          totalEntries: user.validEntries, // Store as total for now, we'll get specific counts per metric
          metrics: {
            resilience: {
              score: user.score,
              rank: user.rank,
              interpretation: user.interpretation,
              validEntries: user.validEntries,
              confidence: user.confidence || { level: 'unknown', message: 'No confidence data' }
            }
          }
        })
      })
    }
    
    return Array.from(userMap.values())
  }

  // Transform confidence-grouped rankings for display
  const transformConfidenceGroupedRankings = (confidenceGroupedRankings, selectedMetric) => {
    if (!confidenceGroupedRankings || !confidenceGroupedRankings[selectedMetric]) {
      return []
    }
    
    const metricMapping = {
      'resilience': 'mentalResilience'
    }
    
    const mappedMetric = metricMapping[selectedMetric] || selectedMetric
    const groupedData = confidenceGroupedRankings[mappedMetric]
    
    return Object.values(groupedData).map(group => ({
      ...group,
      users: group.users.map(user => ({
        userId: user.userId,
        score: user.score,
        rank: user.groupRank, // Use group rank instead of overall rank
        interpretation: user.interpretation,
        validEntries: user.validEntries,
        confidence: user.confidence,
        confidenceGroup: user.confidenceGroup
      }))
    }))
  }
  
  const metrics = [
    { id: 'resilience', label: 'Mental Resilience', icon: FiTrendingUp, color: theme.colors.primary.main },
  ]
  
  if (isLoading) {
    return (
      <Container>
        <LoadingSpinner />
        <BodyText style={{ textAlign: 'center' }}>Loading rankings...</BodyText>
      </Container>
    )
  }
  
  if (error) {
    return (
      <Container>
        <Card style={{ textAlign: 'center', background: theme.colors.status.error, color: 'white' }}>
          <Heading2 style={{ color: 'white', marginBottom: theme.spacing.md }}>
            Error Loading Data
          </Heading2>
          <BodyText style={{ color: 'white', marginBottom: theme.spacing.lg }}>
            {error}
          </BodyText>
          <Button variant="outline" onClick={loadRankingsData} style={{ color: 'white', borderColor: 'white' }}>
            Try Again
          </Button>
        </Card>
      </Container>
    )
  }
  
  // Get confidence-grouped rankings for selected metric
  const confidenceGroups = transformConfidenceGroupedRankings(data.confidenceGroupedRankings, selectedMetric)
  
  // Filter users to only include those with moderate, high, or very high confidence
  const allowedConfidenceLevels = ['moderate', 'high', 'very-high']
  const highConfidenceUsers = data.users.filter(user => {
    const userMetric = user.metrics?.[selectedMetric]
    return userMetric?.score && allowedConfidenceLevels.includes(userMetric?.confidence?.level)
  })
  
  // Traditional sorting for backward compatibility and fallback (filtered by confidence)
  const sortedUsers = dataHelpers.sortUsersByMetric(
    highConfidenceUsers, 
    selectedMetric, 
    sortDirection
  ).slice(0, 50) // Top 50 users
  
  const selectedMetricData = metrics.find(m => m.id === selectedMetric)
  
  return (
    <Container>
      <PageHeader>
        <div>
          <Heading1>User Rankings</Heading1>
          <BodyText>
            Leaderboards for mental resilience metrics (showing only users with reliable data)
          </BodyText>
        </div>
        
        <Button onClick={loadRankingsData} variant="outline">
          <FiRefreshCw style={{ marginRight: '8px' }} />
          Refresh
        </Button>
      </PageHeader>
      
      {/* Metric Selector */}
      <Card>
        <Heading2>Detailed Rankings</Heading2>
        <BodyText style={{ marginBottom: theme.spacing.lg }}>
          View detailed user rankings and scores for mental resilience. Only showing users with moderate, high, or very high confidence levels for reliable comparisons.
        </BodyText>
        
        <MetricSelector>
          {metrics.map((metric) => (
            <MetricButton
              key={metric.id}
              active={selectedMetric === metric.id}
              onClick={() => setSelectedMetric(metric.id)}
            >
              <metric.icon />
              {metric.label}
            </MetricButton>
          ))}
        </MetricSelector>
        
        {/* Confidence-Grouped Rankings */}
        {confidenceGroups.length > 0 ? (
          confidenceGroups.map((group) => (
            <ConfidenceSection key={group.level}>
              <ConfidenceSectionHeader level={group.level}>
                <div>
                  <ConfidenceSectionTitle>{group.displayName}</ConfidenceSectionTitle>
                  <ConfidenceSectionDescription>{group.description}</ConfidenceSectionDescription>
                </div>
                <ConfidenceCount>{group.count} users</ConfidenceCount>
              </ConfidenceSectionHeader>
              
              <RankingTable>
                <TableHeader>
                  <div>Rank</div>
                  <div>User Identity & Journal Data</div>
                  <div>Score & Confidence</div>
                  <div className="hidden-mobile">Status</div>
                </TableHeader>
                
                {group.users.map((user) => {
                  const score = user.score
                  const { interpretation } = dataHelpers.formatScore(score, selectedMetric)
                  const status = dataHelpers.getStatusCategory(score)
                  const isSuperhuman = score >= 85
                  
                  const RowComponent = isSuperhuman ? SuperhumanRow : TableRow
                  
                  return (
                    <RowComponent key={user.userId}>
                      <RankNumber rank={user.rank}>
                        #{user.rank}
                      </RankNumber>
                      
                      <UserInfo>
                        <UserIdentity>
                          <UserDisplayName>
                            {isSuperhuman && '👑 '}
                            {user.userId}
                          </UserDisplayName>
                        </UserIdentity>
                        <UserStats>
                          <StatLine>
                            📊 {user.validEntries || 'N/A'} entries used for {metrics.find(m => m.id === selectedMetric)?.label || selectedMetric}
                          </StatLine>
                          <StatLine>
                            🎯 {user.confidence?.message || 'No confidence data'}
                          </StatLine>
                        </UserStats>
                      </UserInfo>
                      
                      <div>
                        <ScoreDisplay>
                          {Math.round(score)}
                          <ConfidenceBadge level={user.confidence?.level}>
                            {user.confidence?.level?.toUpperCase().replace('-', ' ') || 'UNKNOWN'}
                          </ConfidenceBadge>
                        </ScoreDisplay>
                      </div>
                      
                      <BadgeColumn>
                        <StatusBadge status={status} style={{ fontSize: '10px', padding: '4px 8px' }}>
                          {interpretation.split(' ').slice(0, 2).join(' ')}
                        </StatusBadge>
                      </BadgeColumn>
                    </RowComponent>
                  )
                })}
              </RankingTable>
            </ConfidenceSection>
          ))
        ) : (
          <RankingTable>
            <TableHeader>
              <div>Rank</div>
              <div>User Identity & Journal Data</div>
              <div 
                style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}
                onClick={() => setSortDirection(sortDirection === 'desc' ? 'asc' : 'desc')}
              >
                Score
                {sortDirection === 'desc' ? <FiChevronDown /> : <FiChevronUp />}
              </div>
              <div className="hidden-mobile">Status</div>
            </TableHeader>
            
            {sortedUsers.length === 0 ? (
              <div style={{ padding: theme.spacing.xl, textAlign: 'center', color: theme.colors.primary.light }}>
                No users found with reliable {selectedMetricData?.label.toLowerCase()} data (moderate+ confidence)
              </div>
            ) : (
              sortedUsers.map((user, index) => {
                const rank = index + 1
                const score = user.metrics[selectedMetric].score
                const { interpretation } = dataHelpers.formatScore(score, selectedMetric)
                const status = dataHelpers.getStatusCategory(score)
                const isSuperhuman = score >= 85
                
                const RowComponent = isSuperhuman ? SuperhumanRow : TableRow
                
                return (
                  <RowComponent key={user.userId}>
                    <RankNumber rank={rank}>
                      #{rank}
                    </RankNumber>
                    
                    <UserInfo>
                      <UserIdentity>
                        <UserDisplayName>
                          {isSuperhuman && '👑 '}
                          {user.userId}
                        </UserDisplayName>
                      </UserIdentity>
                      <UserStats>
                        <StatLine>
                          📊 {user.metrics[selectedMetric]?.validEntries || 'N/A'} entries used for {metrics.find(m => m.id === selectedMetric)?.label || selectedMetric}
                        </StatLine>
                        <StatLine>
                          🎯 {user.metrics[selectedMetric]?.confidence?.message || 'No confidence data'}
                        </StatLine>
                      </UserStats>
                    </UserInfo>
                    
                    <div>
                      <ScoreDisplay>
                        {Math.round(score)}
                        <ConfidenceBadge level={user.metrics[selectedMetric]?.confidence?.level}>
                          {user.metrics[selectedMetric]?.confidence?.level?.toUpperCase().replace('-', ' ') || 'UNKNOWN'}
                        </ConfidenceBadge>
                      </ScoreDisplay>
                    </div>
                    
                    <BadgeColumn>
                      {isSuperhuman && (
                        <StatusBadge status="excellent" style={{ fontSize: '10px', padding: '4px 8px', marginBottom: '4px', backgroundColor: theme.colors.status.success }}>
                          👑 SUPERHUMAN
                        </StatusBadge>
                      )}
                      <StatusBadge status={status} style={{ fontSize: '10px', padding: '4px 8px' }}>
                        {interpretation.split(' ').slice(0, 2).join(' ')}
                      </StatusBadge>
                    </BadgeColumn>
                  </RowComponent>
                )
              })
            )}
          </RankingTable>
        )}
        
        {(confidenceGroups.length > 0 || sortedUsers.length > 0) && (
          <SmallText style={{ textAlign: 'center', marginTop: theme.spacing.md, color: theme.colors.primary.light }}>
            {confidenceGroups.length > 0 
              ? `Users with reliable data (moderate+ confidence) grouped by confidence level for ${selectedMetricData?.label.toLowerCase()}` 
              : `Showing top ${sortedUsers.length} users with reliable ${selectedMetricData?.label.toLowerCase()} data (moderate+ confidence)`
            }
          </SmallText>
        )}
      </Card>
      
      {/* Summary Statistics */}
      <Grid columns="repeat(auto-fit, minmax(250px, 1fr))">
        <Card>
          <Heading2>Dataset Summary</Heading2>
          <BodyText>
            <strong>Total Users:</strong> {data.users.length}
          </BodyText>
          <BodyText>
            <strong>Reliable Resilience Data:</strong> {data.users.filter(u => u.metrics?.resilience?.score && ['moderate', 'high', 'very-high'].includes(u.metrics?.resilience?.confidence?.level)).length} / {data.users.filter(u => u.metrics?.resilience?.score).length}
          </BodyText>
        </Card>
        
        <Card>
          <Heading2>Data Quality</Heading2>
          <BodyText>
            <strong>Reliable Complete Profiles:</strong> {
              data.users.filter(u => 
                u.metrics?.resilience?.score && ['moderate', 'high', 'very-high'].includes(u.metrics?.resilience?.confidence?.level)
              ).length
            } users
          </BodyText>
          <BodyText>
            <strong>Data Reliability Rate:</strong> {
              Math.round(
                (data.users.filter(u => 
                  u.metrics?.resilience?.score && ['moderate', 'high', 'very-high'].includes(u.metrics?.resilience?.confidence?.level)
                ).length / Math.max(1, data.users.filter(u => 
                  u.metrics?.resilience?.score
                ).length)) * 100
              )
            }% of complete profiles
          </BodyText>
          <BodyText style={{ marginBottom: '0' }}>
            <strong>Shown in Rankings:</strong> Only users with moderate+ confidence (reliable data)
          </BodyText>
        </Card>
      </Grid>
    </Container>
  )
}

export default RankingsPage 